import type { OutlinedInputProps, TextFieldProps } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { bind } from '@react-rxjs/core';
import { createSignal } from '@react-rxjs/utils';
import { captureException } from '@sentry/browser';
import type { AxiosObservable } from 'axios-observable';
import { Axios } from 'axios-observable';
import type { FormikErrors, FormikProps } from 'formik';
import type { Feature, Point, Position } from 'geojson';
import type { FocusEventHandler, SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';
import { catchError, debounceTime, from, map, mergeMap } from 'rxjs';
import uri from 'uri-tag';

type Props<ID, T> = TextFieldProps &
  OutlinedInputProps & { id: ID; formik: FormikProps<T> };

interface AddressFeature extends Feature<Point> {
  place_name: string;
  place_type: string;
}

const formatPosition = (value?: Position | null): string =>
  value ? `${value[1].toFixed(5)}, ${value[0].toFixed(5)}` : '';

const [textChange$, setText] = createSignal<string>();

const findAddresses = mergeMap(
  (text: string): AxiosObservable<{ features: AddressFeature[] }> => {
    if (!text) {
      return from([{ data: {} }]) as AxiosObservable<{
        features: AddressFeature[];
      }>;
    }

    return Axios.get<{ features: AddressFeature[] }>(
      uri`https://api.mapbox.com/geocoding/v5/mapbox.places/${text}.json?proximity=ip&access_token=${
        import.meta.env.VITE_APP_MAPBOX_TOKEN as string
      }`,
    );
  },
);

const pipe = textChange$.pipe(
  debounceTime(250),
  findAddresses,
  map(({ data }) => data?.features ?? []),
  catchError((error) => {
    captureException(error);
    return [];
  }),
);

const [useAddresses] = bind(pipe, []);

export function FormikLocationField<
  ID extends string,
  T extends { [key in ID]?: Position | null },
>(props: Props<ID, T>): JSX.Element {
  const { formik, fullWidth, id, label, margin, onInvalid, size, variant } =
    props;
  const [_value, setValue] = useState<string | AddressFeature>(
    formatPosition(formik.values[props.id]),
  );
  const [error, setError] = useState<string>();
  const [touched, setTouched] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: works, don't want to mess with it.
  useEffect(() => {
    setValue(formatPosition(formik.values[props.id]));
  }, [formik.values[id]]);

  const addresses = useAddresses();

  const onChange = (
    _: SyntheticEvent<Element, Event>,
    value: string | AddressFeature | null,
  ): void => {
    if (!value) {
      formik.setFieldValue(id, undefined);
      setError(undefined);
      return;
    }

    if (typeof value === 'string') {
      computePoint(value);
      return;
    }

    const coordinates = value.geometry.coordinates;
    formik.setFieldTouched(id, true);
    formik.setFieldValue(id, coordinates, true);
    setValue(formatPosition(coordinates));
    setText('');
    setError(undefined);
  };

  const computePoint = (value: string): void => {
    setTouched(true);

    const parts = value
      .split(',')
      .map((part) => part.trim())
      .map((part) => Number(part))
      .filter(
        (part) => !Number.isNaN(part) && part !== null && part !== undefined,
      );

    if (parts.length !== 2) {
      setError('Expected: <lat>, <long>');

      return;
    }
    if (parts[0] > 90 || parts[0] < -90) {
      setError('Latitude must be between -90 and 90');
      return;
    }
    if (parts[1] > 180 || parts[1] < -180) {
      setError('Longitude must be between -180 and 180');
      return;
    }
    const point = [parts[1], parts[0]];
    formik.setFieldTouched(id, true);
    formik.setFieldValue(id, point, true);
    setValue(formatPosition(point));
    setText('');
    setError(undefined);
  };

  const handleOnBlur: FocusEventHandler<HTMLDivElement> = () => {
    setValue(formatPosition(formik.values[id]));
  };

  return (
    <Autocomplete
      autoComplete
      autoSelect
      clearOnEscape
      filterSelectedOptions
      freeSolo
      fullWidth={fullWidth ?? true}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.place_name;
      }}
      onBlur={handleOnBlur}
      onChange={onChange}
      options={addresses}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={formik.isSubmitting || props.disabled}
          error={
            touched &&
            (!!error || Boolean(props.id in formik.errors) || props.error)
          }
          helperText={error ?? getError(formik.errors[id]) ?? props.helperText}
          id={id}
          label={label}
          margin={margin}
          onChange={({ target }) => setText(target.value)}
          onInvalid={onInvalid}
          size={size}
          variant={variant}
        />
      )}
    />
  );
}

const getError = (
  errors:
    | string
    | string[]
    // biome-ignore lint/suspicious/noExplicitAny: needed
    | FormikErrors<any>
    // biome-ignore lint/suspicious/noExplicitAny: needed
    | FormikErrors<any>[]
    | undefined,
): string | undefined => (errors ? String(errors) : undefined);
